<template>
  <div class="p-4 pb-20"> <!-- 添加底部 padding 防止被 TabBar 遮挡 -->
    <!-- 顶部导航 -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold">今日学习</h1>
      <div class="flex">
        <!-- <i class="fas fa-bell text-gray-500 mr-4 text-xl cursor-pointer" @click="showNotifications"></i> -->
        <router-link to="/settings">
          <i class="fas fa-cog text-gray-500 text-xl cursor-pointer hover:text-blue-600 transition"></i>
        </router-link>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex flex-col justify-center items-center h-80">
      <i class="fas fa-spinner fa-spin text-blue-600 text-3xl mb-4"></i>
      <p class="text-gray-500">正在加载学习数据...</p>
    </div>

    <!-- 数据加载完成 -->
    <div v-else>
      <!-- 活跃的学习计划列表 -->
      <div v-if="activeUserSelections.length > 0" class="space-y-4">
        <h2 class="text-lg font-bold text-gray-800">我的学习计划</h2>
        <div v-for="selection in activeUserSelections" :key="selection.id" class="plan-card bg-white rounded-2xl p-5 shadow-sm transition hover:shadow-lg">
          <!-- 计划标题和基础信息 -->
          <div class="flex justify-between items-start mb-3">
            <div>
              <h3 class="text-lg font-semibold mb-1">{{ selection.name }}</h3>
              <p class="text-xs text-gray-500">基础词库: {{ selection.word_list_name }}</p>
            </div>
             <!-- 可以放一个编辑按钮或状态指示器 -->
            <span class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">学习中</span>
          </div>

          <!-- 今日进度 -->
           <div class="mb-4 border-b pb-3">
             <h4 class="text-sm font-medium text-gray-700 mb-2">今日目标进度</h4>
             <div v-if="dailyStatusLoading[selection.id]" class="text-xs text-gray-400">
                <i class="fas fa-spinner fa-spin mr-1"></i> 加载今日进度...
             </div>
              <div v-else-if="dailyStatus[selection.id]" class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                 <div class="flex justify-between">
                   <span class="text-gray-600">新词:</span>
                   <span class="font-semibold text-blue-600">
                     {{ dailyStatus[selection.id]?.new_words_learned_today ?? 0 }} / {{ selection.daily_new_word_goal }}
                   </span>
                 </div>
                 <div class="flex justify-between">
                   <span class="text-gray-600">复习:</span>
                   <span class="font-semibold text-green-600">
                     {{ dailyStatus[selection.id]?.review_words_done_today ?? 0 }} / {{ selection.daily_review_goal }}
                   </span>
                 </div>
             </div>
             <div v-else class="text-xs text-red-500">无法加载今日进度</div>
           </div>

          <!-- 整体进度 -->
           <div class="mb-4">
             <div class="flex items-center justify-between text-sm mb-1">
               <span class="text-gray-600 font-medium">整体掌握度</span>
                <span v-if="planStats[selection.id]" class="font-semibold text-purple-600">
                  {{ planStats[selection.id]?.completion_percentage?.toFixed(1) ?? 0 }}%
                </span>
                <span v-else class="text-xs text-gray-400"><i class="fas fa-spinner fa-spin mr-1"></i></span>
             </div>
             <div class="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
               <div class="bg-gradient-to-r from-purple-500 to-indigo-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                    :style="{ width: (planStats[selection.id]?.completion_percentage ?? 0) + '%' }"></div>
             </div>
              <div v-if="planStats[selection.id]" class="flex justify-between text-xs text-gray-500 mt-1">
                 <span>已掌握: {{ planStats[selection.id]?.mastered_words ?? 0 }}</span>
                 <span>总词数: {{ planStats[selection.id]?.total_words ?? 0 }}</span>
              </div>
              <div v-else class="text-xs text-gray-400 mt-1 italic">加载整体进度...</div>
           </div>

          <!-- 操作按钮 -->
          <div class="mt-4 text-center">
            <button
              @click="goToPracticeSession(selection.id)"
              class="w-full sm:w-auto bg-blue-600 text-white py-2 px-8 rounded-xl font-medium shadow-md hover:bg-blue-700 hover:shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-0.5"
            >
              开始学习 <i class="fas fa-arrow-right ml-2 text-xs"></i>
            </button>
          </div>
        </div>
      </div>

       <!-- 没有活跃计划时的提示 -->
       <div v-else class="text-center bg-white rounded-2xl p-8 shadow-sm">
          <i class="fas fa-box-open text-gray-300 text-5xl mb-4"></i>
          <p class="text-lg font-medium text-gray-700 mb-2">暂无学习计划</p>
          <p class="text-sm text-gray-500 mb-6">选择一个单词本，开始你的德语学习之旅吧！</p>
          <router-link to="/practice" class="bg-green-500 text-white py-2 px-6 rounded-lg font-semibold hover:bg-green-600 transition">
            <i class="fas fa-plus mr-1"></i> 添加学习计划
          </router-link>
       </div>


      <!-- 学习建议 (Placeholder - 可以根据需要保留或移除) -->
      <h2 class="text-lg font-bold mb-3 mt-8 text-gray-800">学习建议</h2>
      <div class="space-y-3">
        <div v-if="overallStats.review_count > 0" class="suggestion-card bg-white rounded-xl p-4 shadow-sm flex items-center cursor-pointer hover:bg-gray-50 transition" @click="goToReview">
          <div class="suggestion-icon bg-blue-100">
            <i class="fas fa-sync text-blue-600"></i>
          </div>
          <div>
            <h3 class="font-semibold">复习单词</h3>
            <p class="text-gray-500 text-xs">有 {{ overallStats.review_count }} 个单词需要复习</p> <!-- 需要后端提供此数据 -->
          </div>
          <i class="fas fa-chevron-right text-gray-400 ml-auto"></i>
        </div>
        <router-link to="/favorites" class="suggestion-card bg-white rounded-xl p-4 shadow-sm flex items-center hover:bg-gray-50 transition">
          <div class="suggestion-icon bg-red-100">
            <i class="fas fa-heart text-red-600"></i>
          </div>
          <div>
            <h3 class="font-semibold">我的收藏</h3>
            <p class="text-gray-500 text-xs">查看您收藏的重点单词</p>
          </div>
          <i class="fas fa-chevron-right text-gray-400 ml-auto"></i>
        </router-link>
        <div class="suggestion-card bg-white rounded-xl p-4 shadow-sm flex items-center cursor-pointer hover:bg-gray-50 transition" @click="goToDictionary">
          <div class="suggestion-icon bg-purple-100">
            <i class="fas fa-book text-purple-600"></i>
          </div>
          <div>
            <h3 class="font-semibold">探索词典</h3>
            <p class="text-gray-500 text-xs">发现更多德语词汇</p>
          </div>
          <i class="fas fa-chevron-right text-gray-400 ml-auto"></i>
        </div>
      </div>
    </div>
    <BottomLineTip />
  </div>
</template>

<script>
import { learningService, userLearningService } from '../services/index.js'
import { ElMessage } from 'element-plus';
import BottomLineTip from '../components/shared/BottomLineTip.vue';

export default {
  name: 'HomeView',
  components: { BottomLineTip },
  data() {
    return {
      loading: true,
      userSelections: [],        // 存储所有用户选择的计划
      dailyStatus: {},           // 存储每个计划的今日状态 { selectionId: statusData }
      planStats: {},             // 存储每个计划的整体统计 { selectionId: statsData }
      overallStats: {            // 存储整体统计数据（可选）
         review_count: 0 // 示例，需要后端支持
      },
      dailyStatusLoading: {}     // 跟踪每个计划每日状态的加载状态 { selectionId: boolean }
    }
  },
  computed: {
    // 筛选出活跃的学习计划
    activeUserSelections() {
      return this.userSelections.filter(selection => selection.is_active);
    }
  },
  async mounted() {
    this.$store.dispatch('changeNavItem', 'home');
    await this.loadHomeData();
  },
  methods: {
    async loadHomeData() {
      this.loading = true;
      this.dailyStatus = {}; // 重置状态
      this.planStats = {};   // 重置状态
      this.dailyStatusLoading = {}; // 重置加载状态

      try {
        // 1. 获取用户的学习计划列表
        const selectionsResult = await userLearningService.getLearningLists();
        this.userSelections = selectionsResult.word_lists || [];

        // 2. 获取整体统计数据 (包含每个计划的整体进度)
        const statsResult = await learningService.getLearningStatistics();
        this.processOverallStats(statsResult); // 处理整体统计和计划统计

        // 3. 为每个 *活跃* 计划获取今日进度
        const activeSelections = this.userSelections.filter(s => s.is_active);
        if (activeSelections.length > 0) {
           await this.fetchDailyStatusForSelections(activeSelections);
        }

      } catch (error) {
        console.error('获取主页数据失败:', error);
        ElMessage.error(`加载主页数据失败: ${error.response?.data?.error || error.message}`);
      } finally {
        this.loading = false;
      }
    },

    // 处理从 getLearningStatistics 返回的数据
    processOverallStats(statsData) {
       // 存储每个计划的整体统计
       if (statsData && statsData.word_list_stats) {
         statsData.word_list_stats.forEach(stat => {
           this.planStats[stat.selected_word_list_id] = stat;
         });
       }
       // (可选) 存储其他整体统计数据，比如需要复习的总数
       // this.overallStats.review_count = statsData.needs_review_count || 0; // 假设后端提供
    },

    // 并行获取所有活跃计划的今日状态
    async fetchDailyStatusForSelections(selections) {
      const promises = selections.map(async (selection) => {
        this.dailyStatusLoading[selection.id] = true; // 设置加载状态
        try {
          const status = await learningService.getDailyGoalStatus(selection.id);
          this.dailyStatus[selection.id] = status;
        } catch (err) {
          console.error(`获取计划 ${selection.id} 的每日状态失败:`, err);
          this.dailyStatus[selection.id] = null; // 表示获取失败
          ElMessage.warning(`无法加载计划 "${selection.name}" 的今日进度`);
        } finally {
           this.dailyStatusLoading[selection.id] = false; // 取消加载状态
        }
      });
      await Promise.all(promises);
    },

    // 跳转到学习会话页面
    goToPracticeSession(selectionId) {
      this.$router.push({ name: 'practiceSession', params: { selectionId: selectionId } });
    },

    // Placeholder methods for suggestions
    goToReview() {
       // 需要确定复习逻辑，是按计划复习还是整体复习
       ElMessage.info('复习功能正在规划中...');
       // 示例：跳转到第一个需要复习的计划
       // const planToReview = this.activeUserSelections.find(s => this.dailyStatus[s.id]?.review_words_done_today < s.daily_review_goal);
       // if (planToReview) {
       //   this.goToPracticeSession(planToReview.id);
       // } else {
       //   ElMessage.success('今日所有计划的复习目标已完成！');
       // }
    },
    goToDictionary() {
        this.$router.push('/dictionary');
    }
  }
}
</script>

<style scoped>
.plan-card {
  /* 添加一些内边距和圆角 */
  border: 1px solid #e5e7eb; /* Light border */
}

.plan-card:hover .hover\:shadow-lg { /* 确保悬停阴影生效 */
   box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
}

.suggestion-card {
   border: 1px solid #f3f4f6; /* Very light border */
}

.suggestion-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.suggestion-icon i {
  font-size: 18px;
}
</style>